<script lang="ts">
  import './i18n';
  import { isLoading } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import Time from 'date-and-time';

  import { Checkout } from './services/checkout';
  import type { IAuthState } from './etc/types';
  import Popover from './components/templates/Popover.svelte';
  import ModalManager from './components/misc/ModalManager.svelte';
  import { AppState, DEFAULT_APP_STATE, SetNotification } from './etc/stores';
  import Collapsed from './components/collapsed/index.svelte';
  import { Container } from './services/container';
  import { Auth } from './services/auth';
  import { Logger } from './services/logger';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from './services/session-manager';
  import Analytics from './services/analytics';
  import { Business } from './services/business';
  import { Customer } from './services/customer';
  import { Backend } from './services/backend';
  import { ReferralProgram } from './services/referral-program';
  import { SocialRewards } from './services/social-rewards';
  import { FreeCoinsCampaign } from './services/free-coins-campaign';
  import {
    getParamsVisualTest,
    isMobile,
    isProbablyGoogleBot,
    onChangePageWeb
  } from './etc/utils';
  import { FeatureFlag } from './services/feature-flag';
  import { mockState } from './mocks/mockVisualTest';
  import { usePostCheckoutModal } from './etc/hooks/useModal';
  import { CustomerImport } from './services/customer-import';
  import {
    removeViewportMobileWix,
    setViewportMobileWix
  } from './etc/viewport';
  import flagKeys from './etc/flagKeys';
  import sessionKeys from './etc/sessionKeys';
  import { initTheme } from './etc/themes';

  const auth = Container.get(Auth) as Auth;
  const checkout = Container.get(Checkout) as Checkout;
  const business = Container.get(Business) as Business;
  const customer = Container.get(Customer) as Customer;
  const backend = Container.get(Backend) as Backend;
  const freeCoinsCampaign = Container.get(
    FreeCoinsCampaign
  ) as FreeCoinsCampaign;
  const referralProgram = Container.get(ReferralProgram) as ReferralProgram;
  const socialRewards = Container.get(SocialRewards) as SocialRewards;
  const customerImport = Container.get(CustomerImport) as CustomerImport;
  const logger = Container.get(Logger).context('App.svelte') as Logger;
  const session = Container.get(SessionManager) as SessionManager;
  const featureFlags = Container.get(FeatureFlag) as FeatureFlag;

  const SESSION_STORE = 'store';

  const { open: openModal } = usePostCheckoutModal();

  let oldUserId: string;
  const userId = $AppState.website.userId;

  let loading = true;
  let hasError = false;
  let oldPopoverOpened = false;
  let timeoutAux = null;
  let allowSaveStore = false;

  AppState.subscribe(state => {
    if (allowSaveStore)
      session.set(
        SESSION_STORE,
        {
          ...state,
          website: DEFAULT_APP_STATE.website,
          checkout: {},
          customer: {}
        },
        {
          expires: Time.addHours(new Date(), 1),
          saveOn: 'sessionStorage'
        }
      );
  });

  function isShowWelcome() {
    if ($AppState.settings.welcomeEnable === false) return false;

    if (isMobile()) {
      return $AppState.settings.mobileWelcome;
    }

    return $AppState.settings.desktopWelcome;
  }

  function PlayGreetings() {
    if (!session.get(sessionKeys.GREETINGS) && isShowWelcome()) {
      setTimeout(() => {
        SetNotification('greetings');
      }, 5000);
      session.set(sessionKeys.GREETINGS, true);
    }
  }

  function PlayFreeCoinsPopup() {
    if (!session.get(sessionKeys.FREE_COINS_POPUP) && isShowWelcome()) {
      SetNotification('welcomeCoinAvailable');
      session.set(sessionKeys.FREE_COINS_POPUP, true);
    }
  }

  function PlayReferralProgramPopup() {
    if (
      $AppState.customer.id ===
      $AppState.referralReferredState.referringCustomer.id
    )
      SetNotification('referYourselfError');
    else SetNotification('referredCoin');
  }

  async function sendWidgetStats() {
    if (isProbablyGoogleBot()) return;

    const currentDate = new Date().toISOString().slice(0, 10);

    const lastCalledDate = localStorage.getItem('sendWidgetStatsCalledDate');

    // JUST CALL THIS ONE TIME EVERY DAY
    if (lastCalledDate !== currentDate) {
      await backend.createWidgetStats({
        businessId: $AppState.business.id,
        eventDate: currentDate
      });

      localStorage.setItem('sendWidgetStatsCalledDate', currentDate);
    }
  }

  async function notificationCheckout() {
    const isFastRedemption = $AppState.featureFlags.includes(
      flagKeys.FAST_REDEMPTIONS
    );

    if (
      isFastRedemption
        ? auth.isCurrentRemindCodePage()
        : auth.isCurrentCartPage()
    ) {
      const notificationComponent = isFastRedemption
        ? 'fastRedemption'
        : 'remindCheckout';

      if ($AppState.auth.connected && !(await auth.isSoftLogin())) {
        const response = await backend.getCoupons($AppState.business.id);
        if (
          response.coupons &&
          response.coupons.filter(coupon => coupon.status === 'active').length >
            0
        ) {
          AppState.update(state => ({
            ...state,
            coupons: response.coupons
          }));
          setTimeout(() => {
            SetNotification(notificationComponent, true);
          }, 2000);
          return;
        }
      }
      const walletCoins = Number(($AppState.customer.wallet || {}).coins) || 0;
      if (
        (!!$AppState.freeCoinsCampaign.id &&
          !$AppState.freeCoinsCampaign.alreadyRedeemed) ||
        $AppState.referralReferredState.businessReferral?.isActive ||
        walletCoins > 0
      ) {
        setTimeout(() => {
          SetNotification(notificationComponent, true);
        }, 2000);
      }
    }
  }

  function goToCustomerImportLoadingPage() {
    let params = new URLSearchParams(location.search);

    params.delete('twism-campaign');
    params.delete('twism-linkId');

    window.history.replaceState(
      null,
      null,
      window.location.pathname + params.toString()
    );

    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: true,
        popoverRouteOnOpen: {
          route: '/customer-import-loading'
        }
      }
    }));
  }

  function goToCustomerAcquisitionLoadingPage() {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: true,
        popoverRouteOnOpen: {
          route: '/customer-acquisition-loading'
        }
      }
    }));
  }

  const playNotificationMessage = () => {
    const urlParams = new URLSearchParams(location.search);

    /**
     * If there's checkout data then prevent
     * from any notification to be displayed
     * to open `youGotCoins` at checkout flow
     */
    const hideNotification =
      session.get(sessionKeys.CHECKOUT) ||
      getParamsVisualTest().enabled ||
      session.get(AUTH_STORAGE_FLAGS.referral);

    if (
      hideNotification &&
      !urlParams.has('twism-linkId') &&
      !urlParams.has('twism-campaign')
    )
      return;

    if (
      $AppState.customerImportCampaign?.status === 'active' &&
      $AppState.customerImportCampaign?.id &&
      urlParams.has('twism-linkId')
    ) {
      goToCustomerImportLoadingPage();
    }
    if (
      urlParams.has('twism-campaign') &&
      urlParams.get('twism-campaign') === 'customer-acquisition' &&
      !$AppState.auth.connected
    ) {
      goToCustomerAcquisitionLoadingPage();
    } else if (
      $AppState.referralReferredState.businessReferral?.isActive &&
      !referralProgram.referralReferredState
    ) {
      PlayReferralProgramPopup();
    } else if ($AppState.freeCoinsCampaign.id) {
      PlayFreeCoinsPopup();
    } else {
      PlayGreetings();
    }
  };

  const onGAEventCollapsed = () => {
    Analytics.logEvent('click-open', 'closed-state');
  };

  const onClick = (e?: Event) => {
    if (e) {
      e.preventDefault();
    }

    clearTimeout(timeoutAux);
    timeoutAux = setTimeout(() => {
      onGAEventCollapsed();

      if (
        session.get(sessionKeys.LAST_CHECKOUT_ID) &&
        session.get(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL)
      ) {
        AppState.update(state => ({
          ...state,
          checkout: session.get(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL).checkout,
          website: {
            ...state.website,
            ...session.get(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL).website
          }
        }));

        openModal();
      } else {
        AppState.update(state => ({
          ...state,
          website: {
            ...state.website,
            popoverOpen: !state.website.popoverOpen
          }
        }));
      }
    }, 100);
  };

  function getWidgetPosition() {
    return $AppState.settings?.position || 'bottomRight';
  }

  const getUserData = async () => {
    try {
      await auth.loadState();
    } catch (e) {
      AppState.update(state => ({
        ...state,
        auth: {} as IAuthState
      }));

      logger.error(e);
    }
  };

  const getBusinessData = async () => {
    try {
      return await business.loadState();
    } catch (e) {
      hasError = true;
      logger.error(e);
    }
  };

  const getBusinessLocationData = async () => {
    try {
      return await business.loadLocation();
    } catch (e) {
      hasError = true;
      logger.error(e);
    }
  };

  const getFeatureFlags = async () => {
    try {
      return await featureFlags.loadState();
    } catch (e) {
      hasError = true;
      logger.error(e);
    }
  };

  const getCustomerData = async () => {
    try {
      await customer.loadState();
    } catch (e) {
      hasError = true;
      logger.error(e);
    }
  };

  const getFreeCoinsCampaign = async () => {
    try {
      await freeCoinsCampaign.loadState();
    } catch (e) {
      logger.error(e);
    }
  };

  const getReferralProgram = async () => {
    try {
      if ($AppState.referralProgram.isActive === undefined) {
        await referralProgram.loadState();
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const getSocialReward = async () => {
    try {
      if ($AppState.featureFlags.includes(flagKeys.SOCIAL_REWARDS)) {
        await socialRewards.loadState();
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const getCustomerImportCampaign = async () => {
    try {
      await customerImport.loadState();
    } catch (e) {
      logger.error(e);
    }
  };

  const onInitTest = () => {
    const visualParams = getParamsVisualTest();
    if (visualParams.enabled) {
      AppState.update(state => {
        const stateMock = {
          ...state,
          ...mockState
        };
        return {
          ...stateMock,
          website: {
            ...stateMock.website,
            popoverOpen: true,
            popoverRouteOnOpen: {
              route: visualParams.page
            },
            disabledClickPopover: true
          }
        };
      });
    }
  };

  async function initAppState() {
    loading = true;

    const flags = session.get(SESSION_STORE)
      ? session.get(SESSION_STORE).featureFlags
      : [];

    if (!session.get(SESSION_STORE) || !flags.includes(flagKeys.CACHE_API)) {
      const businessLoaded = await getBusinessData();

      if (!businessLoaded || !$AppState.settings.enabled) {
        hasError = true;
        return;
      }
    } else {
      AppState.update(() => session.get(SESSION_STORE));

      initTheme($AppState.settings.color);
      if ($AppState.auth.access_token)
        backend.setBearerToken($AppState.auth.access_token);
    }

    const loging =
      (await auth.isAuthenticatedOnWebsite()) !== $AppState.auth.connected;

    const logingSoftLogin =
      localStorage.getItem(sessionKeys.AUTH_SOFT_LOGIN) &&
      !$AppState.auth.connected;

    const urlParams = new URLSearchParams(location.search);

    const loadingCampaign =
      urlParams.has('twism-campaign') || urlParams.has('twism-linkId');

    const loadApis =
      !session.get(SESSION_STORE) ||
      loging ||
      logingSoftLogin ||
      loadingCampaign ||
      !flags.includes(flagKeys.CACHE_API);

    // Always fetch fresh feature flags before any user data or analytics calls.
    // This prevents: (1) race condition where flags are unknown when track() fires,
    // and (2) stale cached flags bypassing segment-sanitizer for up to 1 hour.
    await getFeatureFlags();

    if (loadApis) {
      await Promise.allSettled([
        getBusinessLocationData(),
        getUserData()
      ]);
    }

    await getCustomerData();

    await Promise.allSettled([
      getFreeCoinsCampaign(),
      getReferralProgram(),
      getCustomerImportCampaign()
    ]);

    getSocialReward();

    if (loadApis) {
      session.set(SESSION_STORE, $AppState, {
        expires: Time.addHours(new Date(), 1),
        saveOn: 'sessionStorage'
      });
    }

    allowSaveStore = true;

    await checkout.onAppInit();

    playNotificationMessage();

    notificationCheckout();

    onInitTest();

    loading = false;

    sendWidgetStats();
  }

  function initEvents() {
    Analytics.init();
    auth.registerWebsiteEvents();
    if (auth.provider === 'wix') onChangePageWeb(notificationCheckout);
  }

  function initViewPortWix() {
    AppState.subscribe(state => {
      if (
        state.website.popoverOpen !== oldPopoverOpened &&
        $AppState.featureFlags.includes(flagKeys.MOBILE_WIX_RESIZE_WINDOW)
      ) {
        if (state.website.popoverOpen) {
          setViewportMobileWix();
        } else {
          removeViewportMobileWix();
        }
      }
      oldPopoverOpened = state.website.popoverOpen;
    });
  }

  onMount(() => {
    logger.initDatadogLibrary();
    initEvents();
    initAppState();
    initViewPortWix();
  });

  $: {
    if (userId && oldUserId !== userId) {
      getUserData();
      oldUserId = userId;
    }
  }
</script>

{#if !loading && !hasError && $AppState.settings.enabled === true && !$isLoading}
  <ModalManager />
  <Collapsed on:touchstart={onClick} on:click={onClick} />
  <Popover direction={getWidgetPosition()} />
{/if}

<style lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  :global(#twism-app-widget) {
    font-size: 10px;
  }

  :global(.twism-app-widget :is(h1, h2, h3, h4, h5, h6, p)) {
    text-transform: none;
    letter-spacing: 0;
    word-wrap: normal;
    outline: none;
    color: inherit;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
    background-color: initial;
  }

  :global(
      .twism-app-widget
        :is(
          article,
          aside,
          details,
          figcaption,
          figure,
          footer,
          header,
          hgroup,
          menu,
          nav,
          section
        )
    ) {
    display: block;
  }

  :global(.twism-app-widget body) {
    line-height: 1;
  }

  :global(.twism-app-widget :is(ol, ul)) {
    list-style: none;
  }

  :global(.twism-app-widget :is(blockquote, q)) {
    quotes: none;
  }

  :global(.twism-app-widget table) {
    border-collapse: collapse;
    border-spacing: 0;
  }

  :global(
      .twism-app-widget
        :is(blockquote:before, blockquote:after, q:before, q:after)
    ) {
    content: '';
    content: none;
  }
  :global(
      .twism-app-widget
        :is(
          div,
          span,
          applet,
          object,
          iframe,
          h1,
          h2,
          h3,
          h4,
          h5,
          h6,
          p,
          blockquote,
          pre,
          a,
          abbr,
          acronym,
          address,
          big,
          cite,
          code,
          del,
          dfn,
          em,
          img,
          ins,
          kbd,
          q,
          s,
          samp,
          small,
          strike,
          strong,
          sub,
          sup,
          tt,
          var,
          b,
          u,
          i,
          center,
          dl,
          dt,
          dd,
          ol,
          ul,
          li,
          fieldset,
          form,
          label,
          legend,
          table,
          caption,
          tbody,
          tfoot,
          thead,
          tr,
          th,
          td,
          article,
          aside,
          canvas,
          details,
          embed,
          figure,
          figcaption,
          footer,
          header,
          hgroup,
          menu,
          nav,
          output,
          ruby,
          section,
          summary,
          time,
          mark,
          audio,
          video
        )
    ) {
    margin: 0;
    padding: 0;
    border: 0;
    vertical-align: baseline;
    display: revert;
  }

  :global(.twism-app-widget img) {
    max-width: inherit;
  }

  :global(.twism-app-widget svg) {
    fill: none;
    stroke: inherit;
    width: unset;
    height: unset;
    vertical-align: baseline;
  }

  :global(#twism-app-widget *) {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  :global(#twism-app-widget *::selection) {
    background-color: transparent;
  }
  :global(#twism-app-widget *:focus-visible) {
    outline-offset: 0;
    box-shadow: none;
  }
</style>
